import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'

import { useDrawer } from 'ducks/drawers/hooks'
import { buildDrawerQueryParam } from 'ducks/drawers/util'
import { doOpenDrawer } from 'ducks/drawers/actions'
import { doFetchWidget } from 'ducks/widgets/operations'

import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import ChannelMembersForm from 'subapps/settings/components/shared/forms/ChannelMembers'
import { selectPendingChannelById } from 'ducks/channels/selectors'
import {
  doSaveChannelDraft,
  doClearChannelDraft,
  doUpdateChannel,
  doCreateChannel,
} from 'ducks/channels/actions'
import { releaseHold, retainHold } from 'util/dirtyHolds'
import { doFetchMailbox } from 'actions/mailboxes'
import { doBuildInboxMenuFromMailboxes } from 'ducks/folders/operations/collections'

import CONFIG from './config'

const EditMembers = ({
  drawer: drawerType,
  drawerId,
  drawerResourceId: channelId,
  drawerChannelType: channelType = 'forwarding',
  onClose,
  onExit,
}) => {
  const dispatch = useDispatch()
  const [isLoading, setIsLoading] = useState(null)
  const [isSaving, setIsSaving] = useState(null)
  const [hasError, setHasError] = useState(false)
  const { drawer } = CONFIG[channelType]

  const { drawerId: nextDrawerId, openDrawer: openNextDrawer } = useDrawer(
    drawer
  )

  const draftChannel = useSelector(state =>
    selectPendingChannelById(state, channelId)
  )

  // Ensure we load a fresh copy of mailbox and hydrate the draft
  // with the latest information
  useEffect(
    () => {
      if (isLoading !== null) return

      // Kevin R
      // This is what the linter said I should do
      async function loadAndSetupDraft() {
        setIsLoading(true)
        let resource = null
        let fetchedChannelId = null

        const isNewChannel = channelId === 'new'

        if (channelType === 'messenger') {
          resource = await dispatch(doFetchWidget(channelId))
          fetchedChannelId = channelId
        } else {
          if (isNewChannel && draftChannel && channelType === 'forwarding') {
            resource = await dispatch(doCreateChannel(draftChannel))
          }
          fetchedChannelId = isNewChannel ? resource?.id : channelId
          if (fetchedChannelId && fetchedChannelId !== 'new') {
            resource = await dispatch(
              doFetchMailbox(fetchedChannelId, { skipLoaded: false })
            )
          }
        }

        if (!resource) {
          releaseHold(drawerId)
          onClose()
        } else if (isNewChannel) {
          dispatch(doBuildInboxMenuFromMailboxes())
          dispatch(
            doOpenDrawer(drawerId, drawerType, fetchedChannelId, {
              query: {
                ...buildDrawerQueryParam(
                  drawerId,
                  'drawerChannelType',
                  channelType
                ),
              },
            })
          )
          setIsLoading(null)
        } else {
          dispatch(doSaveChannelDraft(fetchedChannelId, resource))
          setIsLoading(false)
        }
      }
      loadAndSetupDraft()
    },
    [
      dispatch,
      channelId,
      isLoading,
      onClose,
      drawerType,
      draftChannel,
      drawerId,
      channelType,
    ]
  )

  const handleOpenNextDrawer = useCallback(
    (_, clearDraft = true) => {
      releaseHold(drawerId)
      openNextDrawer(channelId, {
        query: {
          ...buildDrawerQueryParam(
            nextDrawerId,
            'drawerChannelType',
            channelType
          ),
        },
      })
      if (clearDraft) {
        dispatch(doClearChannelDraft(channelId))
      }
    },
    [dispatch, openNextDrawer, drawerId, channelId, nextDrawerId, channelType]
  )

  const handleSaveAndOpenNextDrawer = useCallback(
    async e => {
      try {
        releaseHold(drawerId)
        setIsSaving(true)
        setHasError(false)
        await dispatch(
          doUpdateChannel(channelId, draftChannel, { channelType })
        )
        setIsSaving(false)
        handleOpenNextDrawer(e, false)
      } catch (error) {
        retainHold(drawerId)
        setIsSaving(false)
        setHasError(true)
      }
    },
    [
      dispatch,
      drawerId,
      channelId,
      draftChannel,
      channelType,
      handleOpenNextDrawer,
    ]
  )

  const handleChange = useCallback(
    fields => {
      retainHold(drawerId)
      dispatch(doSaveChannelDraft(channelId, fields))
    },
    [dispatch, channelId, drawerId]
  )

  const footer = useMemo(
    () => {
      return (
        <ModalBtns
          saveBtnText={
            isSaving ? (
              <span>
                Saving<AnimatedEllipsis />
              </span>
            ) : (
              'Continue'
            )
          }
          onSave={handleSaveAndOpenNextDrawer}
          saveBtnDisabled={isSaving}
          saveBtnTestId="save-and-continue-button"
          tertiaryBtnText="I'll do this later"
          onClickTertiaryBtn={handleOpenNextDrawer}
          tertiaryBtnDisabled={isSaving}
        />
      )
    },
    [handleSaveAndOpenNextDrawer, handleOpenNextDrawer, isSaving]
  )

  const handleOnHide = useCallback(() => {
    setIsLoading(null)
  }, [])

  return (
    <AdminAccessDrawer
      title={`${app.t('Agent')} access`}
      footer={footer}
      onClose={onExit}
      onHide={handleOnHide}
      isLoading={isLoading}
      data-test-id="channels-members-edit"
    >
      <ChannelMembersForm
        channelId={channelId}
        defaultRestrictionType={draftChannel?.restriction_type}
        defaultUserIds={draftChannel?.user_ids}
        defaultGroupIds={draftChannel?.group_ids}
        onChange={handleChange}
        showError={hasError}
      />
    </AdminAccessDrawer>
  )
}

export default EditMembers
